import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, first, map, take } from 'rxjs/operators';
import { ApiService } from 'src/app/auth-o3/shared/services/api.service';
import { environment } from 'src/environments/environment';
import {
  WorkflowData, WorkflowManagementDecisionPayload,
  WorkflowManagementTaskDecision,
  WorkflowManagementTaskDtos,
} from '../model/interfaces/workflows.interface';
import { NotificationsService } from '../../notifications/notification.service';

const API_ROOT_AS = environment.workflow.api.workflowEngine + '/';

@Injectable({
  providedIn: 'root',
})
export class WorkflowsService {
  constructor(private apiService: ApiService,
    private notificationsService: NotificationsService
  ) { }

  getDataFromBackend(id, type): Observable<WorkflowData> {
    return this.getData(id, type);
  }

  getData(id: string | number, type: string): Observable<WorkflowData> {
    let workflowType = '';
    switch (type) {
      case 'multiissuance':
        workflowType = 'multiIssuanceFlow/multiIssuanceFlow';
        break;
      case 'singleissuance':
        workflowType = 'multiIssuanceManual/multiIssuanceManual';
        break;
      case 'supplementAgreement':
        workflowType = 'feeSupplement';
        break;
      case 'supplementAgreementRequest':
        workflowType = 'feeSupplementAgreementRequest';
        break;
      default:
        workflowType = type;

    }

    return this.apiService.get(
      `api/workflowManagement/${id}-${workflowType}`,
      API_ROOT_AS
    ).pipe(first<WorkflowData>());
  }

  getUserApprovalTasks(
    context: string,
    businessKey?: string
  ): Observable<WorkflowManagementTaskDtos[]> {
    return this.apiService
      .get<WorkflowManagementTaskDtos[]>(
        `api/usertasks/${businessKey ? (businessKey + '/') : ''}${context}/ugTasks`,
        API_ROOT_AS
      )
      .pipe(
        catchError(() => {
          return of([]);
        }),
        map((tasks: WorkflowManagementTaskDtos[]) => {
          return tasks.map((task) => {
            return {
              ...task,
              decisions: (task.decisions as string).split(','),
            };
          });
        }),
        take(1)
      );
  }

  postTaskDecision(
    taskDefinitionKey,
    businessKey,
    decision
  ): Observable<WorkflowManagementTaskDecision> {
    return this.apiService.post(
      `api/usertasks/${taskDefinitionKey}/progress/${businessKey}/${decision}`,
      {},
      API_ROOT_AS
    );
  }

  postTaskNewDecision(payload: WorkflowManagementDecisionPayload): Observable<WorkflowManagementTaskDecision> {
    return this.apiService.postWithHeaders(
      `api/usertasks/progress`,
      payload,
      API_ROOT_AS,
      {
        ...this.apiService.getOptions().headers,
        connectionId: this.notificationsService.connectionId
      }
    );
  }

  getWorkflowStateImage(id: number, type: string): Observable<any> {

    let workflowType = '';
    switch (type) {
      case 'multiissuance':
        workflowType = 'multiIssuanceFlow';
        break;
      case 'singleissuance':
        workflowType = 'multiIssuanceManual';
        break;
      case 'supplementAgreement':
        workflowType = 'feeSupplement';
        break;
      case 'supplementAgreementRequest':
        workflowType = 'feeSupplementAgreementRequest';
        break;
      default:
        workflowType = type;
    }

    return this.apiService
      .getWithOpts<Blob>(
        `api/processes/history/diagram?businessKey=${id}-${workflowType}`,
        API_ROOT_AS,
        {
          ...this.apiService.getOptions(),
          responseType: 'blob',
        }
      )
      .pipe(first());
  }

  getStaticDataApprovalTasks(
    context: string
  ): Observable<WorkflowManagementTaskDtos[]> {
    return this.apiService
      .get<WorkflowManagementTaskDtos[]>(
        `api/usertasks/${context}/ugTasks`,
        API_ROOT_AS
      )
      .pipe(
        catchError(() => {
          return of([]);
        }),
        map((tasks: WorkflowManagementTaskDtos[]) => {
          return tasks.map((task) => {
            return {
              ...task,
              decisions: (task.decisions as string).split(','),
            };
          });
        }),
        take(1)
      );
  }

  getWorkflowAudits(businessKey: string): Observable<WorkflowData> {
    return this.apiService
      .get<WorkflowData>(
        `api/workflowManagement/${businessKey}/audit`,
        API_ROOT_AS
      )
      .pipe(first());
  }
}
